import clsx from "clsx";
import { ChevronUp, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";

import { Spinner } from "../../components/EmptyState";
import { ApiError } from "../../api/client";
import { isNeutral } from "../../enemyDisplay";
import type { Phase } from "../../api/types";
import { unitDesignation } from "../../i18n/designation";
import { k, t } from "../../i18n/i18n";
import { missionName, term } from "../../i18n/vocabulary";
import { plural } from "../../plural";
import { useUi } from "../../state/ui";
import { symbolSvg } from "./symbols";
import { useDeployment } from "./useDeployment";
import { Waiting } from "../../components/Waiting";
import { Menu, MenuItem, MenuLabel, MenuSeparator } from "../../components/Menu";
import { motion, usePresence, useLastDefined } from "../../components/presence";
import { useZoomLevels } from "./zoomLevels";
import { postureLabel } from "./propagation";
import { rotationCode, rotationName } from "../../centacRotation";
import { useOpenDossier } from "../../sharing/access";

const PHASES: { key: Phase; hint: string }[] = [
  { key: "T1", hint: k("Temps 1 — débouché") },
  { key: "T2", hint: k("Temps 2 — conduite") },
  { key: "T3", hint: k("Temps 3 — fin de mission") },
];

const OPOS = [1, 2, 3, 4];

/** Barre des temps (bas de la carte) : force commandée, mission, et choix du temps T1 / T2 / T3.
 *  CENTAC : choix de l'OPO de la rotation (1 à 4) ; chaque temps montre une phase de l'OPO.
 *  Touches 1, 2, 3 pour changer de temps. `showTitle` : faux dans une bande trop étroite (moins d'environ 520 px) —
 *  le bloc titre se masque au lieu d'être tronqué, le titre passant dans la bulle et le nom du symbole. */
export function PhaseBar({ showTitle = true }: { showTitle?: boolean }) {
  const params = useUi((s) => s.deploymentParams);
  const phase = useUi((s) => s.phase);
  const setPhase = useUi((s) => s.setPhase);
  const opo = useUi((s) => s.opo);
  const setOpo = useUi((s) => s.setOpo);
  const { data, error, isFetching } = useDeployment();
  // Rotation CENTAC : nom de la rotation jouée, celui du dossier enregistré affiché (registre A119).
  const dossier = useOpenDossier();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const next = ({ "1": "T1", "2": "T2", "3": "T3" } as Record<string, Phase>)[e.key];
      if (next) setPhase(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setPhase]);

  if (!params) return null;
  const message = error instanceof ApiError ? error.message : error ? t("Déploiement indisponible") : null;
  const isCentac = params.level === "CENTAC";
  const rotation = isCentac ? data?.centac : null;
  const played = isCentac && params.exercise_id && dossier?.id === params.exercise_id ? rotationCode(dossier.parameters) : null;
  // ENI personnalisé du dossier ouvert (registre A135) : son nom de code à la place de l'ennemi porteur.
  const enemy = (params.exercise_id && dossier?.id === params.exercise_id ? dossier.custom_enemy_code : null) ?? data?.enemy_type;
  const title = !data
    ? t("Dispositif")
    : isCentac
      ? played
        ? `${unitDesignation(data.force_label)} — ${rotationName(played)}`
        : t("{force} — rotation CENTAC", { force: unitDesignation(data.force_label) })
      : `${unitDesignation(data.force_label)} — ${missionName(data.posture?.operation ?? data.mission)}`;
  // Posture des unités (docs/25) : AMI et ENI au temps affiché ; mission de l'ordre tirée des missions quand elle
  // n'est plus celle des Données initiales.
  const posture = !isCentac && data?.posture ? data.posture : null;
  // ui-check: ignore-start — libellé d'attente affiché par <Waiting> là où il est rendu (sous-titre de la barre, plus bas ; texte brut dans la bulle)
  const subtitle = !data
    ? t("Calcul du dispositif…")
    : rotation
      ? t("OPO {opo} — {mission} · J+{day} · dispositif sur {lima} · FORAD {enemy}", {
          opo: rotation.opo,
          mission: missionName(rotation.mission),
          day: rotation.day_offset,
          lima: rotation.lima,
          enemy: data.enemy_type,
        })
      : posture
        ? t("Posture AMI : {ami} · ENI : {eni}", {
            ami: postureLabel(posture.ami[phase]),
            eni: postureLabel(posture.eni[phase]),
          })
        : isNeutral(data.reference_enemy)
          ? // Force non française : l'ennemi générique, jamais un ennemi de référence (registre B39).
            t("{mode} · ennemi générique", { mode: term(data.mode) })
          : t("{mode} · ennemi {enemy}", { mode: term(data.mode), enemy });
  const postureHint = posture?.changed
    ? t("Posture tirée des missions des unités : l'ordre est rédigé pour « {operation} » (Données initiales : « {dossier} »).", {
        operation: missionName(posture.operation),
        dossier: missionName(posture.dossier_operation),
      })
    : undefined;
  // ui-check: ignore-end

  return (
    <div
      className={clsx(
        "pointer-events-auto flex h-10 items-center gap-3 rounded-xl border border-line bg-surface/95 pr-1 shadow-float backdrop-blur",
        showTitle || !data ? "pl-3" : "pl-1.5",
      )}
    >
      {data && (
        <span
          role="img"
          aria-label={title}
          title={showTitle ? undefined : `${title}\n${message ?? subtitle}`}
          className="flex size-7 shrink-0 items-center justify-center"
          dangerouslySetInnerHTML={{ __html: symbolSvg(data.force_sidc, 22) }}
        />
      )}
      {(showTitle || !data || message) && (
        <div className="flex min-w-0 flex-col leading-tight">
          {(showTitle || !data) && <span className="truncate text-title font-semibold text-ink">{title}</span>}
          <span
            title={postureHint}
            className={clsx("truncate text-meta transition-colors", posture?.changed ? "font-medium text-accent" : "text-ink-muted")}
          >
            {message ? (
              <span className="inline-flex items-center gap-1 text-danger">
                <TriangleAlert size={12} /> {message}
              </span>
            ) : (
              <Waiting>{subtitle}</Waiting>
            )}
          </span>
        </div>
      )}
      {isFetching && <Spinner />}
      {isCentac && (
        <div className="flex items-center rounded-lg bg-canvas p-0.5" role="tablist" aria-label={t("OPO de la rotation")}>
          {OPOS.map((n) => (
            <button
              key={n}
              type="button"
              role="tab"
              aria-selected={opo === n}
              title={t("OPO {n} de la rotation", { n })}
              onClick={() => setOpo(n)}
              className={clsx(
                "h-8 rounded-md px-2 text-ui font-semibold whitespace-nowrap tabular-nums transition-colors",
                opo === n ? "bg-surface text-accent shadow-sm" : "text-ink-muted hover:text-ink",
              )}
            >
              {t("OPO {n}", { n })}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center rounded-lg bg-canvas p-0.5" role="tablist" aria-label={t("Temps de la manœuvre")}>
        {PHASES.map((p) => {
          const step = rotation?.phases[p.key];
          const hint = !rotation
            ? t(p.hint)
            : step?.in_rotation
              ? step.detail
                ? t("{phase} de l'OPO {opo} — J+{day} {detail}", {
                    phase: p.key,
                    opo: rotation.opo,
                    day: step.day,
                    detail: term(step.detail),
                  })
                : t("{phase} de l'OPO {opo} — J+{day}", { phase: p.key, opo: rotation.opo, day: step.day })
              : t("Hors de l'OPO {opo} ({phases}) : missions par défaut « S'emparer »", {
                  opo: rotation.opo,
                  phases: plural(rotation.phase_count, t("phase"), t("phases")),
                });
          return (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={phase === p.key}
              // Cible désignée quand une ligne de la ZO glissée bute sur une ligne d'un autre temps (docs/15 §4.3).
              data-attention={`phase-${p.key}`}
              title={t("{hint} (touche {key})", { hint, key: p.key.slice(1) })}
              onClick={() => setPhase(p.key)}
              className={clsx(
                "flex h-8 flex-col items-center justify-center rounded-md px-2.5 text-ui font-semibold tabular-nums transition-colors",
                rotation ? "min-w-16" : "min-w-11", // même largeur pour les trois temps ; « hors OPO » se lit, sans grisé
                phase === p.key ? "bg-surface text-accent shadow-sm" : "text-ink-muted hover:text-ink",
              )}
            >
              <span className="leading-none">{p.key}</span>
              {rotation && (
                <span className="mt-0.5 text-caps leading-none font-medium text-ink-muted">
                  {step?.in_rotation ? t("J+{day}", { day: step.day }) : t("hors OPO")}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <LevelChip />
    </div>
  );
}

/** Niveau affiché par la carte zoomée (registres J11, J16) : repère discret à droite des temps, en fondu —
 *  « Niveau affiché : S/GTIA » — ou, à sa place, l'attente quand le zoom demande un niveau que le serveur prépare
 *  encore. Présent dès qu'un niveau inférieur existe (niveau du dossier compris). Un appui ouvre le choix du niveau :
 *  selon le zoom (par défaut) ou fixé à la main, quel que soit le zoom (« Niveau fixé : GTIA »). */
function LevelChip() {
  const label = useZoomLevels((s) => s.label);
  const waiting = useZoomLevels((s) => s.waiting);
  const options = useZoomLevels((s) => s.options);
  const manual = useZoomLevels((s) => s.manual);
  const depth = useZoomLevels((s) => s.depth);
  const setManual = useZoomLevels((s) => s.setManual);
  const [open, setOpen] = useState(false);
  // ui-check: ignore-start — libellé d'attente affiché par <Waiting> plus bas
  // L'attente prime sur le niveau affiché : zoomer plus loin pendant que le niveau suivant se prépare le dit, au
  // lieu de laisser « Niveau affiché : GTIA » sans explication (registre J14).
  const text = waiting
    ? t("Préparation des niveaux inférieurs…")
    : label
      ? manual !== null
        ? t("Niveau fixé : {level}", { level: t(label) })
        : t("Niveau affiché : {level}", { level: t(label) })
      : null;
  // ui-check: ignore-end
  const shown = useLastDefined(text);
  const { mounted, closing } = usePresence(Boolean(text) && (options.length > 1 || waiting));
  if (!mounted || !shown) return null;
  return (
    <Menu
      open={open}
      onClose={() => setOpen(false)}
      side="top"
      align="end"
      width={260}
      ariaLabel={t("Niveau des unités")}
      wrapperClassName={clsx("inline-flex", motion("fade", closing))}
      trigger={
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={clsx(
            "flex h-8 items-center gap-1 rounded-lg px-2.5 text-meta font-medium whitespace-nowrap transition-colors",
            manual !== null ? "bg-accent-soft text-accent" : "bg-canvas text-ink-muted hover:bg-hover",
          )}
          title={t("Unités des ordres déclinés : zoomez pour descendre d'un niveau, dézoomez pour regrouper")}
          aria-label={t("Choisir le niveau des unités")}
          data-zoom-level={depth}
        >
          <Waiting>{shown}</Waiting>
          <ChevronUp size={12} aria-hidden className="shrink-0 opacity-70" />
        </button>
      }
    >
      <MenuLabel>{t("Niveau des unités")}</MenuLabel>
      <MenuItem
        label={t("Selon le zoom")}
        hint={t("Zoomez pour descendre d'un niveau, dézoomez pour regrouper")}
        selected={manual === null}
        onSelect={() => setManual(null)}
      />
      <MenuSeparator />
      {options.map((option) => (
        <MenuItem
          key={option.depth}
          label={t(option.label)}
          selected={manual === option.depth}
          onSelect={() => setManual(option.depth)}
        />
      ))}
    </Menu>
  );
}
